import { useMemo, useState } from "react";
import { api, ApiError, errorMessage } from "../../api";
import type { AdminMember, OrgRole } from "../../api/types";
import { Avatar, EmptyState, ErrorBox, Modal, Spinner } from "../../components/bits";
import { Icon } from "../../components/icons";
import { formatDateTime } from "../../lib/format";
import { loadMembers, toast, toastError } from "../../store/app";
import { useApp, useMyActorId } from "../../store/hooks";
import { ConfirmDialog, Forbidden, OneTimeSecret, ROLES, useOrgData } from "./shared";

function AddMemberDialog({
  orgId,
  roles,
  onClose,
  onAdded,
}: {
  orgId: string;
  roles: readonly string[];
  onClose: () => void;
  onAdded: (m: AdminMember) => void;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<OrgRole>("member");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [temp, setTemp] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  return (
    <Modal title="Add member" onClose={onClose}>
      {done ? (
        <div className="form">
          <p>
            <strong>{name || email}</strong> was added as {role}.
          </p>
          {temp ? (
            <OneTimeSecret label="Temporary password" value={temp} />
          ) : (
            <p className="muted small">They can sign in with the password you set.</p>
          )}
          <div className="form-actions">
            <button type="button" className="btn primary" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      ) : (
        <form
          className="form"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError(null);
            try {
              const res = await api.admin.addMember(orgId, {
                email: email.trim(),
                display_name: name.trim() || email.trim(),
                role,
                ...(password ? { password } : {}),
              });
              setTemp(res.temporary_password);
              setDone(true);
              onAdded(res.member);
            } catch (err) {
              setError(errorMessage(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          <label className="field">
            <span>Email</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
          </label>
          <label className="field">
            <span>Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Display name" />
          </label>
          <label className="field">
            <span>Role</span>
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              {roles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Password (optional)</span>
            <input
              type="password"
              value={password}
              autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Leave empty to generate a temporary password"
            />
          </label>
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <div className="form-actions">
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={busy}>
              {busy ? "Adding…" : "Add member"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function MergeDialog({
  orgId,
  members,
  initialSource,
  onClose,
  onMerged,
}: {
  orgId: string;
  members: AdminMember[];
  initialSource?: string;
  onClose: () => void;
  onMerged: () => void;
}) {
  const [source, setSource] = useState(initialSource ?? "");
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label = (m: AdminMember) =>
    `${m.actor.display_name} (${m.actor.kind}${m.actor.primary_email ? `, ${m.actor.primary_email}` : ""})`;
  const src = members.find((m) => m.actor.id === source);
  const tgt = members.find((m) => m.actor.id === target);
  return (
    <Modal title="Merge people" onClose={onClose}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!source || !target || source === target) return;
          setBusy(true);
          setError(null);
          try {
            await api.admin.mergeActors(orgId, { source_actor_id: source, target_actor_id: target });
            toast("People merged", "success");
            onMerged();
          } catch (err) {
            const msg = errorMessage(err);
            setError(
              err instanceof ApiError && err.status === 409
                ? `${msg} – the source has a login; merge the other way round (pick the ghost/remote person as source).`
                : msg,
            );
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className="muted small">
          Moves assignments and linked tool identities from the source to the target person. The source is kept for history
          and marked as merged.
        </p>
        <label className="field">
          <span>Source (merged away)</span>
          <select value={source} onChange={(e) => setSource(e.target.value)} required>
            <option value="">— choose —</option>
            {members
              .filter((m) => !m.actor.merged_into)
              .map((m) => (
                <option key={m.actor.id} value={m.actor.id}>
                  {label(m)}
                </option>
              ))}
          </select>
        </label>
        <div className="merge-arrow" aria-hidden="true">
          <Icon name="chevronDown" />
        </div>
        <label className="field">
          <span>Target (kept)</span>
          <select value={target} onChange={(e) => setTarget(e.target.value)} required>
            <option value="">— choose —</option>
            {members
              .filter((m) => m.actor.id !== source && !m.actor.merged_into)
              .map((m) => (
                <option key={m.actor.id} value={m.actor.id}>
                  {label(m)}
                </option>
              ))}
          </select>
        </label>
        {src && tgt && (
          <p className="small">
            <strong>{src.actor.display_name}</strong>’s {src.identities} identit{src.identities === 1 ? "y" : "ies"} and
            assignments move to <strong>{tgt.actor.display_name}</strong>.
          </p>
        )}
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn primary" disabled={busy || !source || !target}>
            {busy ? "Merging…" : "Merge"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

type Dialog =
  | { kind: "add" }
  | { kind: "merge"; source?: string }
  | { kind: "remove"; member: AdminMember }
  | { kind: "reset"; member: AdminMember }
  | { kind: "secret"; member: AdminMember; password: string };

export function MembersTab() {
  const { orgId, data, setData, error, forbidden, reload } = useOrgData((id) => api.admin.members(id));
  const myActorId = useMyActorId();
  const isOwner = useApp((s) => s.me?.orgs.find((o) => o.org.id === s.orgId)?.role === "owner");
  // Only owners can grant/revoke the owner role.
  const grantableRoles = isOwner ? ROLES : ROLES.filter((r) => r !== "owner");
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const members = useMemo(() => data?.members ?? [], [data]);
  const q = query.trim().toLowerCase();
  const match = (m: AdminMember) =>
    !q ||
    m.actor.display_name.toLowerCase().includes(q) ||
    (m.account?.email ?? m.actor.primary_email ?? "").toLowerCase().includes(q);
  const isPerson = (m: AdminMember) => !m.actor.merged_into && (m.actor.kind === "member" || m.actor.kind === "guest" || !!m.account);
  const people = members.filter((m) => isPerson(m) && match(m));
  const others = members.filter((m) => !isPerson(m) && match(m));
  const nameOf = (id: string) => members.find((x) => x.actor.id === id)?.actor.display_name ?? "another person";

  if (forbidden) return <Forbidden />;
  if (!orgId) return null;

  const replace = (m: AdminMember) =>
    setData((d) => (d ? { members: d.members.map((x) => (x.actor.id === m.actor.id ? m : x)) } : d));

  const patch = async (m: AdminMember, body: Parameters<typeof api.admin.patchMember>[2], ok: string) => {
    setBusy(m.actor.id);
    try {
      replace(await api.admin.patchMember(orgId, m.actor.id, body));
      toast(ok, "success");
      void loadMembers(true).catch(() => undefined);
    } catch (e) {
      toastError(e, "Update failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section aria-labelledby="members-title" className="admin-section">
      <div className="section-head">
        <h2 id="members-title">Members</h2>
        <div className="section-actions">
          <input
            type="search"
            className="search"
            placeholder="Filter by name or email"
            aria-label="Filter members"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button type="button" className="btn" onClick={() => setDialog({ kind: "merge" })}>
            Merge people…
          </button>
          <button type="button" className="btn primary" onClick={() => setDialog({ kind: "add" })}>
            <Icon name="plus" size={14} />
            Add member
          </button>
        </div>
      </div>
      {error && <ErrorBox error={error} onRetry={reload} />}
      {!data && !error ? (
        <Spinner />
      ) : (
        <>
          <div className="table-wrap">
            <table className="table members-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last login</th>
                  <th title="Connections">Conn.</th>
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {people.map((m) => {
                  const self = m.actor.id === myActorId;
                  const suspended = m.account?.status === "suspended";
                  return (
                    <tr key={m.actor.id} className={suspended ? "suspended" : ""}>
                      <td>
                        <span className="person-cell">
                          <Avatar actor={m.actor} size={26} />
                          <span>
                            {m.actor.display_name}
                            {self && <span className="muted small"> (you)</span>}
                          </span>
                        </span>
                      </td>
                      <td className="small">{m.account?.email ?? m.actor.primary_email ?? "—"}</td>
                      <td>
                        <select
                          aria-label={`Role of ${m.actor.display_name}`}
                          value={m.role ?? ""}
                          disabled={busy === m.actor.id || (m.role === "owner" && !isOwner)}
                          title={m.role === "owner" && !isOwner ? "Only owners can change an owner's role" : undefined}
                          onChange={(e) => void patch(m, { role: e.target.value }, "Role updated")}
                        >
                          {!m.role && <option value="">— removed —</option>}
                          {(m.role === "owner" && !isOwner ? ROLES : grantableRoles).map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {m.account && m.role === null ? (
                          <span className="chip status-suspended" title="Deprovisioned">removed</span>
                        ) : m.account ? (
                          <span className={`chip status-${m.account.status}`}>{m.account.status}</span>
                        ) : (
                          <span className="muted small">no account</span>
                        )}
                      </td>
                      <td className="small">{m.account?.last_login_at ? formatDateTime(m.account.last_login_at) : "never"}</td>
                      <td className="num">{m.connections}</td>
                      <td>
                        <div className="row-actions">
                          {m.account && (
                            <button
                              type="button"
                              className="btn small"
                              disabled={busy === m.actor.id || self}
                              onClick={() =>
                                void patch(
                                  m,
                                  { status: suspended ? "active" : "suspended" },
                                  suspended ? "Member reactivated" : "Member suspended",
                                )
                              }
                            >
                              {suspended ? "Reactivate" : "Suspend"}
                            </button>
                          )}
                          {m.account && (
                            <button
                              type="button"
                              className="btn small"
                              disabled={busy === m.actor.id}
                              onClick={() => setDialog({ kind: "reset", member: m })}
                            >
                              Reset password
                            </button>
                          )}
                          {m.role !== null && (
                            <button
                              type="button"
                              className="btn small danger"
                              disabled={busy === m.actor.id || self}
                              title={self ? "You cannot remove yourself" : undefined}
                              onClick={() => setDialog({ kind: "remove", member: m })}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {people.length === 0 && <EmptyState title="No members match" />}

          <h3 className="subhead">Ghost, remote &amp; merged people</h3>
          <p className="muted small">
            People known only from connected tools (no account). Merge duplicates into a real member to combine their
            assignments.
          </p>
          {others.length === 0 ? (
            <p className="muted small">None.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Kind</th>
                    <th>Email</th>
                    <th>Identities</th>
                    <th>
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {others.map((m) => (
                    <tr key={m.actor.id} className={m.actor.merged_into ? "suspended" : ""}>
                      <td>
                        <span className="person-cell">
                          <Avatar actor={m.actor} size={26} />
                          {m.actor.display_name}
                        </span>
                      </td>
                      <td>
                        <span className="chip">{m.actor.kind}</span>
                        {m.actor.merged_into && (
                          <div className="muted small">merged into {nameOf(m.actor.merged_into)}</div>
                        )}
                      </td>
                      <td className="small">{m.actor.primary_email ?? "—"}</td>
                      <td className="num">{m.identities}</td>
                      <td>
                        {!m.actor.merged_into && (
                          <button
                            type="button"
                            className="btn small"
                            onClick={() => setDialog({ kind: "merge", source: m.actor.id })}
                          >
                            Merge into…
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {dialog?.kind === "add" && (
        <AddMemberDialog
          orgId={orgId}
          roles={grantableRoles}
          onClose={() => setDialog(null)}
          onAdded={() => {
            reload(); // a matching ghost may have been converted
            void loadMembers(true).catch(() => undefined);
          }}
        />
      )}
      {dialog?.kind === "merge" && (
        <MergeDialog
          orgId={orgId}
          members={members}
          initialSource={dialog.source}
          onClose={() => setDialog(null)}
          onMerged={() => {
            setDialog(null);
            reload();
            void loadMembers(true).catch(() => undefined);
          }}
        />
      )}
      {dialog?.kind === "remove" && (
        <ConfirmDialog
          title={`Remove ${dialog.member.actor.display_name}?`}
          confirmLabel="Remove member"
          danger
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            try {
              await api.admin.removeMember(orgId, dialog.member.actor.id);
              toast(`${dialog.member.actor.display_name} removed`, "success");
              setDialog(null);
              reload();
              void loadMembers(true).catch(() => undefined);
            } catch (e) {
              toastError(e, "Remove failed");
            }
          }}
        >
          <p>Removing a member deprovisions their access:</p>
          <ul className="small">
            <li>their account is suspended and all sessions end,</li>
            <li>their personal connections (and the sync bindings using them) are deleted,</li>
            <li>organization connections they own are reassigned to you.</li>
          </ul>
          <p className="muted small">
            The person stays in history: past assignments, comments and the audit log keep their name.
          </p>
        </ConfirmDialog>
      )}
      {dialog?.kind === "reset" && (
        <ConfirmDialog
          title={`Reset password for ${dialog.member.actor.display_name}?`}
          confirmLabel="Reset password"
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            try {
              const r = await api.admin.resetPassword(orgId, dialog.member.actor.id);
              setDialog({ kind: "secret", member: dialog.member, password: r.temporary_password });
            } catch (e) {
              toastError(e, "Reset failed");
            }
          }}
        >
          <p>A new temporary password is generated and all of their sessions end.</p>
        </ConfirmDialog>
      )}
      {dialog?.kind === "secret" && (
        <Modal title={`New password for ${dialog.member.actor.display_name}`} onClose={() => setDialog(null)}>
          <div className="form">
            <OneTimeSecret label="Temporary password" value={dialog.password} />
            <div className="form-actions">
              <button type="button" className="btn primary" onClick={() => setDialog(null)}>
                Done
              </button>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
}
